import React from 'react';
import Accordion from '../Accordion';
import CodeBlock from '../CodeBlock';
import CheckIcon from '../CustomIcons/CheckIcon';
import RejectIcon from '../CustomIcons/RejectIcon';
import Tag from '../Tag';
import type { TagType } from '../Tag';
import Typography from '../Typography';

export type ChatToolCallStatus = 'running' | 'completed' | 'error';

export interface ChatToolCallProps {
    /** The tool the assistant called, e.g. `update_policy_draft`. */
    name: string;
    status?: ChatToolCallStatus;
    /**
     * The provider's id for this call. Shown once the card is expanded, not on the collapsed
     * row: it is a long opaque string nobody reads at a glance, and beside the tool name it
     * was taking two lines of the transcript to say nothing a reader wanted.
     */
    callId?: string;
    /** Arguments the assistant sent. */
    input?: object;
    /** What the call returned. Absent while it is still running. */
    output?: object;
    defaultExpanded?: boolean;
    className?: string;
}

const STATUS_LABELS: Record<ChatToolCallStatus, string> = {
    running: 'Running',
    completed: 'Completed',
    error: 'Failed',
};

const STATUS_TAG_TYPES: Record<ChatToolCallStatus, TagType> = {
    running: 'warning',
    completed: 'success',
    error: 'error',
};

/** Only a settled call carries a mark; a running one would be claiming an outcome. */
const STATUS_ICONS: Partial<Record<ChatToolCallStatus, React.ReactNode>> = {
    completed: <CheckIcon size={11} />,
    error: <RejectIcon size={11} />,
};

/**
 * One tool call in a transcript, collapsed by default: what the assistant did is worth a
 * line, and the arguments behind it are worth a click.
 *
 * The status sits in `trailing` rather than in the header, so it stays outside the toggle
 * button — a tag nested inside a button is invalid, and it reads as its own thing.
 */
const ChatToolCall: React.FC<ChatToolCallProps> = ({
    name,
    status = 'completed',
    callId,
    input,
    output,
    defaultExpanded = false,
    className,
}) => (
    <Accordion
        variant="sunken"
        defaultExpanded={defaultExpanded}
        className={className}
        header={
            <span className="min-w-0 text-left">
                <Typography.Text size="small" fontWeight="semi-bold">
                    {name}
                </Typography.Text>
            </span>
        }
        trailing={
            <Tag
                value={STATUS_LABELS[status]}
                type={STATUS_TAG_TYPES[status]}
                fontSize="xsmall"
                textTransform="none"
                icon={STATUS_ICONS[status]}
                iconPosition="left"
            />
        }
    >
        <div className="flex flex-col gap-2 p-2">
            {callId === undefined ? null : (
                <Typography.SecondaryText size="xsmall">Call ID: {callId}</Typography.SecondaryText>
            )}
            {input === undefined ? null : <CodeBlock json={input} collapsed={2} />}
            {output === undefined ? null : <CodeBlock json={output} collapsed={2} />}
        </div>
    </Accordion>
);

export default ChatToolCall;
export { ChatToolCall };
